import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { GridColDef } from '@mui/x-data-grid';
import { Box, Button, Tooltip, Typography, useTheme } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { BiExport } from 'react-icons/bi';
import { LuInfo } from 'react-icons/lu';
import dayjs from 'dayjs';

import { AppDataGrid, ButtonTitleWithLoading, ConfirmDialog } from '@vyce/core/src/components';
import { formatDate, getUKFormattedDate } from '@vyce/core/src/utils/dates';
import {
  approveByPeriodRequest,
  getTimeLogsByPeriodRequest,
  postExportTimeLogsRequest,
} from '@vyce/core/src/api/time';
import {
  TIME_INTERFACE_PERMISSIONS,
  TimeLogByPeriod,
  TimeLogBySite,
  TimeLogStatus,
} from '@vyce/core/src/types';
import { useBooleanState } from '@vyce/core/src/hooks';
import { downloadFileByUrl } from '@vyce/core/src/utils';

import { useStyles } from './styles';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { NotificationContext } from '../../../contexts/notificationContext';
import { CustomArrowPagination } from '../../../components/CustomPagination/CustomArrowPagination';

const PAGE_SIZE = 8;
const defaultDate = formatDate(new Date());

export const getStatusTitle = (status: TimeLogStatus) => {
  switch (status) {
    case TimeLogStatus.APPROVED:
      return 'Approved';
    case TimeLogStatus.NOT_APPROVED:
      return 'Awaiting';
    case TimeLogStatus.PARTLY_APPROVED:
      return 'Partly Approved';
    default:
      return status;
  }
};

export const StatusComponent = ({ status }: { status: TimeLogStatus }) => {
  const theme = useTheme();

  return (
    <Typography
      variant="caption"
      style={{
        fontWeight: 500,
        color:
          status === TimeLogStatus.NOT_APPROVED
            ? theme.palette.error.main
            : status === TimeLogStatus.PARTLY_APPROVED
            ? theme.palette.warning.main
            : theme.palette.success.main,
      }}>
      {getStatusTitle(status)}
    </Typography>
  );
};

export const ExportButtonComponent = ({
  onClick,
  log,
}: {
  log: TimeLogByPeriod | TimeLogBySite;
  onClick: Function;
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);

  const handleClick = async () => {
    try {
      setLoading(true);
      await onClick(log);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      startIcon={loading ? undefined : <BiExport size="15px" />}
      variant="contained"
      disabled={!log.total_hours_decimals || loading}
      className={classes.exportButton}
      size="small"
      color="secondary">
      <ButtonTitleWithLoading title="Export" loading={loading} loaderVariant="paper" size={20} />
    </Button>
  );
};

export const ApproveButtonComponent = ({
  log,
  onClick,
}: {
  log: TimeLogByPeriod | TimeLogBySite;
  onClick: Function;
}) => {
  const classes = useStyles();

  const handleClick = () => {
    onClick(log);
  };

  const tooltipText = useMemo(() => {
    if (log.approved_by?.length) {
      return `Approved by: ${log.approved_by?.join(', ') ?? ''} on ${dayjs(log.approved_at).format(
        'DD/MM/YYYY'
      )} at ${dayjs(log.approved_at).format('HH:mm')}`;
    }
    return '';
  }, [log.approved_by, log.approved_at]);

  return (
    <>
      {log.status === TimeLogStatus.APPROVED ? (
        <Tooltip title={tooltipText}>
          <Box
            display="flex"
            alignItems="center"
            gridGap={4}
            justifyContent="center"
            className={classes.approveButton}>
            <Typography variant="button">Approved</Typography>
            <LuInfo size="15px" />
          </Box>
        </Tooltip>
      ) : (
        <Button
          size="small"
          onClick={handleClick}
          variant="contained"
          color="primary"
          className={classes.approveButton}>
          Approve All
        </Button>
      )}
    </>
  );
};

interface Props {
  companyId?: string;
}

export const TimeLogsByPeriod: React.FC<Props> = ({ companyId }) => {
  const { can, canOnAnyResource } = usePermissions();
  const theme = useTheme();
  const history = useHistory();
  const { handleServerError } = useContext(NotificationContext);
  const [timeLogs, setTimeLogs] = useState<TimeLogByPeriod[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [approveLoading, setApproveLoading] = useState<boolean>(false);
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] = useBooleanState(false);
  const [selectedLog, setSelectedLog] = useState<TimeLogByPeriod>();
  const [pivotDates, setPivotDates] = useState<string[]>([defaultDate]);

  const controlPivotDates = useCallback(
    (page: number) => {
      if (pivotDates.length === 1) return;
      if (page >= pivotDates.length - 1) {
        return getTimeLogs({ pivotDate: pivotDates[pivotDates.length - 1] });
      }
      if (page < pivotDates.length - 1) {
        setPivotDates(value => {
          const newValue = [...value];
          newValue.pop();
          return newValue;
        });
        getTimeLogs({ pivotDate: pivotDates[pivotDates.length - 3], skipSavingDate: true });
      }
    },
    [pivotDates]
  );

  // A period row aggregates every location, so there is no id to check against:
  // show the column when approval is possible anywhere. The per-location
  // drill-down does the real gating, and the server enforces it on the call.
  const cnaApproveTimelogs =
    can(TIME_INTERFACE_PERMISSIONS.APPROVE) ||
    canOnAnyResource(TIME_INTERFACE_PERMISSIONS.APPROVE, 'location');

  const columns: GridColDef[] = [
    {
      field: 'period',
      headerName: 'For Period',
      width: 100,
      flex: 0.15,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Box
          display="flex"
          style={{ cursor: 'pointer' }}
          flexDirection="column"
          onClick={() => history.push(`/time/timelogs/${row.period?.start} - ${row.period?.end}`)}>
          <Typography color="secondary" variant="caption" style={{ fontWeight: 500 }}>
            {getUKFormattedDate(row.period?.start)}
          </Typography>
          <Typography color="secondary" variant="caption" style={{ fontWeight: 500 }}>
            {getUKFormattedDate(row.period?.end)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'workers',
      headerName: 'Workers',
      disableColumnMenu: true,
      flex: 0.1,
      minWidth: 100,
    },
    {
      field: 'clocked_in_hours_decimals',
      headerName: 'Clocked Hours',
      disableColumnMenu: true,
      flex: 0.1,
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="caption" style={{ fontWeight: 500 }}>
          {row.clocked_in_hours_decimals}
        </Typography>
      ),
    },
    {
      field: 'basic_hours_decimals',
      headerName: 'Regular Paid Hours',
      disableColumnMenu: true,
      flex: 0.1,
      minWidth: 160,
      renderCell: ({ row }) => (
        <Typography variant="caption" style={{ fontWeight: 500 }}>
          {row.basic_hours_decimals}
        </Typography>
      ),
    },
    {
      field: 'overtime_hours_decimals',
      headerName: 'OT Paid Hours',
      disableColumnMenu: true,
      flex: 0.1,
      minWidth: 150,
      renderCell: ({ row }) => (
        <Typography variant="caption" style={{ fontWeight: 500 }}>
          {row.overtime_hours_decimals}
        </Typography>
      ),
    },
    {
      field: 'total_hours_decimals',
      headerName: 'Total Paid Hours',
      disableColumnMenu: true,
      flex: 0.1,
      minWidth: 160,
      renderCell: ({ row }) => (
        <Typography variant="caption" style={{ fontWeight: 500 }}>
          {row.total_hours_decimals}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.1,
      minWidth: 100,
      disableColumnMenu: true,
      renderCell: ({ row }) => <StatusComponent status={row.status} />,
    },
    {
      field: 'controls',
      headerName: 'Actions',
      width: 290,
      hideSortIcons: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Box display="flex" gridGap={16} width="100%">
          <ExportButtonComponent onClick={exportTimelogs} log={row as TimeLogByPeriod} />
          <ApproveButtonComponent log={row as TimeLogByPeriod} onClick={handleApproveButtonClick} />
        </Box>
      ),
    },
  ];

  const permissionBasedColumns = useMemo(() => {
    if (cnaApproveTimelogs) {
      return columns;
    }
    return columns.filter(col => col.field !== 'controls');
  }, [cnaApproveTimelogs]);

  const handleApproveButtonClick = (log: TimeLogByPeriod) => {
    setSelectedLog(log);
    openConfirmDialog();
  };

  const getTimeLogs = useCallback(
    async ({
      pivotDate = defaultDate,
      skipSavingDate = false,
    }: {
      pivotDate?: string;
      skipSavingDate?: boolean;
    }) => {
      if (!companyId) {
        return;
      }
      try {
        const { data } = await getTimeLogsByPeriodRequest(companyId, {
          next_pivot_date: pivotDate,
        });
        setTimeLogs(data.items);
        setLoading(false);

        if (!skipSavingDate) {
          setPivotDates(value => {
            if (value.includes(data.next_pivot_date)) return value;
            const newValue = [...value, data.next_pivot_date];
            return newValue;
          });
        }
      } catch (e) {
        setLoading(false);
      }
    },
    [companyId]
  );

  const exportTimelogs = async (log: TimeLogByPeriod) => {
    if (!companyId) {
      return;
    }
    try {
      const { data } = await postExportTimeLogsRequest({
        companyId,
        data: { period: log.period },
      });
      downloadFileByUrl(data.url, data.file_name);
    } catch (e) {
      handleServerError(e);
    }
  };

  const approve = async () => {
    if (!companyId || !selectedLog?.period) {
      return;
    }
    try {
      setApproveLoading(true);
      await approveByPeriodRequest(companyId, selectedLog.period);
      getTimeLogs({});
      setApproveLoading(false);
      closeConfirmDialog();
    } catch (e) {
      handleServerError(e);
      setApproveLoading(false);
    }
  };

  const paginationOptions = useMemo(
    () => ({
      page: pivotDates.length - 2 >= 0 ? pivotDates.length - 2 : 0,
      pageSize: PAGE_SIZE,
      pageCount: pivotDates.length + 1,
      rowCount: PAGE_SIZE,
      setPage: controlPivotDates,
    }),
    [pivotDates.length, controlPivotDates]
  );

  useEffect(() => {
    getTimeLogs({});
  }, [getTimeLogs]);

  return (
    <>
      <AppDataGrid
        noPaper
        rows={timeLogs}
        loading={loading}
        height="calc(100vh - 225px)"
        getRowId={row => row.period?.start + row.period?.end}
        columns={permissionBasedColumns}
        rowCount={timeLogs.length}
        sortingMode="server"
        disableSelectionOnClick
        hideFooterPagination
        hideFooter
      />
      <Box padding="8px 0" bgcolor={theme.palette.background.paper}>
        <CustomArrowPagination {...paginationOptions} />
      </Box>

      <ConfirmDialog
        handleClose={closeConfirmDialog}
        open={isConfirmDialogOpen}
        confirmText="Approve"
        title="Do you want to approve this Time log?"
        loading={approveLoading}
        handleConfirm={approve}
      />
    </>
  );
};
